import React, { useRef, useEffect, useState } from 'react';

/**
 * Extended rich text editor for news bulletins
 * Supports: Bold, Italic, Underline, Ordered List, Unordered List, Todo List, H1-H4 headings
 */
const RichTextEditorExtended = ({ value, onChange, placeholder = 'Enter text...', className = '' }) => {
  const editorRef = useRef(null);
  const isUpdatingRef = useRef(false);
  const [currentBlockType, setCurrentBlockType] = useState('p');
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const pendingLinkRange = useRef(null);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [linkPopupPosition, setLinkPopupPosition] = useState({ top: 0, left: 0 });

  // Initialize content
  useEffect(() => {
    if (editorRef.current && !isUpdatingRef.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current && onChange) {
      isUpdatingRef.current = true;
      onChange(editorRef.current.innerHTML);
      updateCurrentBlockType();
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    }
  };

  const updateCurrentBlockType = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      let node = selection.anchorNode;

      // Traverse up to find the block element
      while (node && node !== editorRef.current) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName?.toLowerCase();
          if (['h1', 'h2', 'h3', 'h4', 'p'].includes(tagName)) {
            setCurrentBlockType(tagName);
            return;
          }
        }
        node = node.parentNode;
      }
      setCurrentBlockType('p');
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const insertList = (type) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    let node = range.commonAncestorContainer;

    // If it's a text node, get its parent
    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode;
    }

    // Check if we're already in ANY list (regular or todo) - if so, toggle it off
    let listElement = node;
    while (listElement && listElement !== editorRef.current) {
      const tagName = listElement.tagName?.toLowerCase();
      if (tagName === 'ol' || tagName === 'ul') {
        // We're in a list, convert it back to text with line breaks
        const listItems = Array.from(listElement.querySelectorAll('li'));

        // Create a container div with text nodes and <br> tags
        const container = document.createElement('div');

        listItems.forEach((li, index) => {
          // Extract text from span if exists (for todo lists), otherwise from li
          const span = li.querySelector('span');
          const textContent = span ? span.textContent : li.textContent;
          const textNode = document.createTextNode(textContent);
          container.appendChild(textNode);

          // Add <br> after each line except the last
          if (index < listItems.length - 1) {
            container.appendChild(document.createElement('br'));
          }
        });

        // Replace the list with the container
        listElement.parentNode.replaceChild(container, listElement);

        // Move cursor to end of container
        const newRange = document.createRange();
        newRange.selectNodeContents(container);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);

        handleInput();
        return;
      }
      listElement = listElement.parentNode;
    }

    // Not in a list, create one
    // Extract all block-level elements from the selection
    const fragment = range.cloneContents();
    const wrapper = document.createElement('div');
    wrapper.appendChild(fragment);

    // Extract text from each block-level or text node
    let lines = [];

    const extractLines = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) lines.push(text);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName?.toLowerCase();

        // If it's a block element, extract its text as a line
        if (['div', 'p', 'h1', 'h2', 'h3', 'h4', 'li'].includes(tag)) {
          const text = node.textContent.trim();
          if (text) lines.push(text);
        } else if (tag === 'br') {
          // BR marks a line break
        } else {
          // Recurse into inline elements
          node.childNodes.forEach(child => extractLines(child));
        }
      }
    };

    // Process all children
    wrapper.childNodes.forEach(node => extractLines(node));

    // If no lines found, use default
    if (lines.length === 0) {
      lines = ['List item'];
    }

    // Create list
    const listTag = type === 'ordered' ? 'ol' : 'ul';
    const list = document.createElement(listTag);

    lines.forEach(lineText => {
      const li = document.createElement('li');
      li.textContent = lineText;
      list.appendChild(li);
    });

    // Delete selected content and insert list
    range.deleteContents();
    range.insertNode(list);

    // Move cursor to end of first list item
    const firstLi = list.querySelector('li');
    if (firstLi) {
      const newRange = document.createRange();
      newRange.selectNodeContents(firstLi);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    handleInput();
  };

  const toggleHeading = (level) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    let node = range.commonAncestorContainer;

    // If it's a text node, get its parent
    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode;
    }

    // Find the closest block element (h1-h4 or p)
    let blockElement = node;
    while (blockElement && blockElement !== editorRef.current) {
      if (blockElement.nodeType === Node.ELEMENT_NODE) {
        const tagName = blockElement.tagName?.toLowerCase();
        if (['h1', 'h2', 'h3', 'h4', 'p', 'div'].includes(tagName)) {
          break;
        }
      }
      blockElement = blockElement.parentNode;
    }

    // If we found a block element to transform
    if (blockElement && blockElement !== editorRef.current) {
      const currentTag = blockElement.tagName?.toLowerCase();

      // Determine the new tag: if clicking the same level, convert to div; otherwise convert to the level
      let newTag;
      if (currentTag === level) {
        newTag = 'div';
      } else {
        newTag = level;
      }

      // Create new element with the same content
      const newElement = document.createElement(newTag);
      newElement.innerHTML = blockElement.innerHTML;

      // Replace the old element
      blockElement.parentNode.replaceChild(newElement, blockElement);

      // Restore cursor position
      const newRange = document.createRange();
      newRange.selectNodeContents(newElement);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);
    } else {
      // No block element found, create a new heading with default or selected text
      const selectedText = range.toString() || 'Heading';
      const newElement = document.createElement(level);
      newElement.textContent = selectedText;

      if (range.toString()) {
        range.deleteContents();
      }
      range.insertNode(newElement);

      // Move cursor to end of new element
      const newRange = document.createRange();
      newRange.selectNodeContents(newElement);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    handleInput();
    updateCurrentBlockType();
  };

  const insertTodoList = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    let node = range.commonAncestorContainer;

    // If it's a text node, get its parent
    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentNode;
    }

    // Check if we're already in ANY list (regular or todo) - if so, toggle it off
    let listElement = node;
    while (listElement && listElement !== editorRef.current) {
      const tagName = listElement.tagName?.toLowerCase();
      if (tagName === 'ol' || tagName === 'ul') {
        // We're in a list, convert it back to text with line breaks
        const listItems = Array.from(listElement.querySelectorAll('li'));

        // Create a container div with text nodes and <br> tags
        const container = document.createElement('div');

        listItems.forEach((li, index) => {
          // Extract text from span if exists (for todo lists), otherwise from li
          const span = li.querySelector('span');
          const textContent = span ? span.textContent : li.textContent;
          const textNode = document.createTextNode(textContent);
          container.appendChild(textNode);

          // Add <br> after each line except the last
          if (index < listItems.length - 1) {
            container.appendChild(document.createElement('br'));
          }
        });

        // Replace the list with the container
        listElement.parentNode.replaceChild(container, listElement);

        // Move cursor to end of container
        const newRange = document.createRange();
        newRange.selectNodeContents(container);
        newRange.collapse(false);
        selection.removeAllRanges();
        selection.addRange(newRange);

        handleInput();
        return;
      }
      listElement = listElement.parentNode;
    }

    // Not in any list, create a todo list
    // Extract all block-level elements from the selection
    const fragment = range.cloneContents();
    const wrapper = document.createElement('div');
    wrapper.appendChild(fragment);

    // Extract text from each block-level or text node
    let lines = [];

    const extractLines = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) lines.push(text);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName?.toLowerCase();

        // If it's a block element, extract its text as a line
        if (['div', 'p', 'h1', 'h2', 'h3', 'h4', 'li'].includes(tag)) {
          const text = node.textContent.trim();
          if (text) lines.push(text);
        } else if (tag === 'br') {
          // BR marks a line break
        } else {
          // Recurse into inline elements
          node.childNodes.forEach(child => extractLines(child));
        }
      }
    };

    // Process all children
    wrapper.childNodes.forEach(node => extractLines(node));

    // If no lines found, use default
    if (lines.length === 0) {
      lines = ['Todo item'];
    }

    // Create todo list with multiple items
    const todoList = document.createElement('ul');
    todoList.className = 'todo-list';

    lines.forEach(lineText => {
      const li = document.createElement('li');
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      const span = document.createElement('span');
      span.contentEditable = 'true';
      span.textContent = lineText;

      li.appendChild(checkbox);
      li.appendChild(document.createTextNode(' '));
      li.appendChild(span);
      todoList.appendChild(li);
    });

    // Delete selected content and insert todo list
    range.deleteContents();
    range.insertNode(todoList);

    // Move cursor to the first todo item
    const firstSpan = todoList.querySelector('li span');
    if (firstSpan) {
      const newRange = document.createRange();
      newRange.selectNodeContents(firstSpan);
      newRange.collapse(false);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }

    handleInput();
  };

  const insertLink = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    if (!selectedText) {
      alert('Please select some text to create a link.');
      return;
    }

    // Store the range and show modal
    pendingLinkRange.current = range.cloneRange();
    setLinkUrl('https://');
    setShowLinkModal(true);
  };

  const handleLinkSubmit = () => {
    if (!linkUrl || linkUrl === 'https://') {
      setShowLinkModal(false);
      setLinkUrl('');
      pendingLinkRange.current = null;
      return;
    }

    const range = pendingLinkRange.current;
    if (!range) return;

    const selection = window.getSelection();
    const selectedText = range.toString();

    // Create link element
    const link = document.createElement('a');
    link.href = linkUrl;
    link.target = '_blank';
    link.textContent = selectedText;

    // Delete selected content and insert link
    range.deleteContents();
    range.insertNode(link);

    // Move cursor after the link
    const newRange = document.createRange();
    newRange.setStartAfter(link);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);

    handleInput();

    // Reset modal state
    setShowLinkModal(false);
    setLinkUrl('');
    pendingLinkRange.current = null;
  };

  const handleLinkCancel = () => {
    setShowLinkModal(false);
    setLinkUrl('');
    pendingLinkRange.current = null;
  };

  const handleMouseMove = (e) => {
    // Check if hovering over a link
    const target = e.target;
    if (target.tagName?.toLowerCase() === 'a') {
      const rect = target.getBoundingClientRect();
      const editorRect = editorRef.current.getBoundingClientRect();

      setHoveredLink(target);
      setLinkPopupPosition({
        top: rect.top - editorRect.top - 35, // Position above the link
        left: rect.left - editorRect.left
      });
    } else {
      // Check if mouse is over the popup itself
      const popup = document.querySelector('.link-hover-popup');
      if (popup && popup.contains(e.target)) {
        // Keep popup visible
        return;
      }
      setHoveredLink(null);
    }
  };

  const handleRemoveLink = () => {
    if (!hoveredLink) return;

    const parent = hoveredLink.parentNode;
    while (hoveredLink.firstChild) {
      parent.insertBefore(hoveredLink.firstChild, hoveredLink);
    }
    parent.removeChild(hoveredLink);

    setHoveredLink(null);
    handleInput();
  };

  const handleKeyDown = (e) => {
    // Handle keyboard shortcuts
    if (e.ctrlKey || e.metaKey) {
      switch (e.key.toLowerCase()) {
        case 'b':
          e.preventDefault();
          execCommand('bold');
          break;
        case 'i':
          e.preventDefault();
          execCommand('italic');
          break;
        case 'u':
          e.preventDefault();
          execCommand('underline');
          break;
        case 'k':
          e.preventDefault();
          insertLink();
          break;
        default:
          break;
      }
    }

    // Handle Enter key in todo lists
    if (e.key === 'Enter' && e.target.closest('.todo-list')) {
      e.preventDefault();
      const li = e.target.closest('li');
      if (li) {
        const newLi = document.createElement('li');
        newLi.innerHTML = '<input type="checkbox" /> <span contenteditable="true"></span>';
        li.parentNode.insertBefore(newLi, li.nextSibling);

        // Focus on the new span
        const newSpan = newLi.querySelector('span');
        const range = document.createRange();
        range.selectNodeContents(newSpan);
        range.collapse(true);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        handleInput();
      }
    }
  };

  const handleClick = (e) => {
    // Handle checkbox clicks in todo lists
    if (e.target.type === 'checkbox' && e.target.closest('.todo-list')) {
      // Update the checked attribute to match the checked property so it persists in innerHTML
      if (e.target.checked) {
        e.target.setAttribute('checked', 'checked');
      } else {
        e.target.removeAttribute('checked');
      }
      handleInput();
    }
    updateCurrentBlockType();
  };

  return (
    <div className={`rich-text-editor-extended ${className}`}>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 border-b border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t">
        {/* Heading buttons */}
        <button
          type="button"
          onClick={() => toggleHeading('h1')}
          className={`px-3 py-1 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${currentBlockType === 'h1' ? 'bg-primary-200 dark:bg-primary-700' : 'bg-white dark:bg-gray-700'} text-primary`}
          title="Heading 1"
        >
          H1
        </button>
        <button
          type="button"
          onClick={() => toggleHeading('h2')}
          className={`px-3 py-1 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${currentBlockType === 'h2' ? 'bg-primary-200 dark:bg-primary-700' : 'bg-white dark:bg-gray-700'} text-primary`}
          title="Heading 2"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => toggleHeading('h3')}
          className={`px-3 py-1 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${currentBlockType === 'h3' ? 'bg-primary-200 dark:bg-primary-700' : 'bg-white dark:bg-gray-700'} text-primary`}
          title="Heading 3"
        >
          H3
        </button>
        <button
          type="button"
          onClick={() => toggleHeading('h4')}
          className={`px-3 py-1 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${currentBlockType === 'h4' ? 'bg-primary-200 dark:bg-primary-700' : 'bg-white dark:bg-gray-700'} text-primary`}
          title="Heading 4"
        >
          H4
        </button>

        <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>

        {/* Format buttons */}
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="px-3 py-1 text-sm font-bold border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Bold (Ctrl+B)"
        >
          B
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="px-3 py-1 text-sm italic border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Italic (Ctrl+I)"
        >
          I
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="px-3 py-1 text-sm underline border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Underline (Ctrl+U)"
        >
          U
        </button>
        <button
          type="button"
          onClick={insertLink}
          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Link (Ctrl+K)"
        >
          🔗
        </button>

        <div className="w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>

        {/* List buttons */}
        <button
          type="button"
          onClick={() => insertList('ordered')}
          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Ordered List"
        >
          1. List
        </button>
        <button
          type="button"
          onClick={() => insertList('unordered')}
          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Unordered List"
        >
          • List
        </button>
        <button
          type="button"
          onClick={insertTodoList}
          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-200 dark:hover:bg-gray-600 bg-white dark:bg-gray-700 text-primary"
          title="Todo List"
        >
          ☑ Todo
        </button>
      </div>

      {/* Editor */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          onMouseMove={handleMouseMove}
          className="p-3 min-h-[200px] bg-white dark:bg-gray-800 text-primary border border-gray-300 dark:border-gray-600 rounded-b focus:outline-none focus:ring-2 focus:ring-primary-500"
          data-placeholder={placeholder}
          style={{
            minHeight: '200px',
          }}
        />

        {/* Link Hover Popup */}
        {hoveredLink && (
          <div
            className="link-hover-popup absolute bg-gray-900 text-white text-xs rounded shadow-lg px-3 py-2 z-10 flex items-center gap-2"
            style={{
              top: `${linkPopupPosition.top}px`,
              left: `${linkPopupPosition.left}px`,
            }}
            onMouseEnter={() => {
              // Keep popup visible when hovering over it
            }}
          >
            <a
              href={hoveredLink.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-300 hover:text-blue-200 underline max-w-xs truncate"
              onClick={(e) => e.stopPropagation()}
            >
              {hoveredLink.href}
            </a>
            <button
              onClick={handleRemoveLink}
              className="ml-2 px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
              title="Remove link"
            >
              Remove
            </button>
          </div>
        )}
      </div>

      <style>{`
        .rich-text-editor-extended [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .rich-text-editor-extended [contenteditable] {
          outline: none;
        }
        .rich-text-editor-extended [contenteditable] h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.67em 0;
        }
        .rich-text-editor-extended [contenteditable] h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.75em 0;
        }
        .rich-text-editor-extended [contenteditable] h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin: 0.83em 0;
        }
        .rich-text-editor-extended [contenteditable] h4 {
          font-size: 1em;
          font-weight: bold;
          margin: 1em 0;
        }
        .rich-text-editor-extended [contenteditable] p {
          margin: 0.5em 0;
        }
        .rich-text-editor-extended [contenteditable] ul,
        .rich-text-editor-extended [contenteditable] ol {
          margin: 0.5em 0;
          padding-left: 2em;
        }
        .rich-text-editor-extended [contenteditable] ul {
          list-style-type: disc;
        }
        .rich-text-editor-extended [contenteditable] ol {
          list-style-type: decimal;
        }
        .rich-text-editor-extended [contenteditable] li {
          margin: 0.25em 0;
          display: list-item;
        }
        .rich-text-editor-extended [contenteditable] ul.todo-list {
          list-style: none;
          padding-left: 0;
        }
        .rich-text-editor-extended [contenteditable] ul.todo-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.5em;
        }
        .rich-text-editor-extended [contenteditable] ul.todo-list input[type="checkbox"] {
          margin-top: 0.25em;
          cursor: pointer;
        }
        .rich-text-editor-extended [contenteditable] ul.todo-list li span {
          flex: 1;
        }

        /* View mode (disabled checkboxes) */
        .rich-text-editor-extended [contenteditable] ul.todo-list input[type="checkbox"]:disabled {
          cursor: default;
        }

        /* Links */
        .rich-text-editor-extended [contenteditable] a {
          color: #3b82f6;
          text-decoration: underline;
          cursor: pointer;
        }
        .rich-text-editor-extended [contenteditable] a:hover {
          color: #2563eb;
        }
      `}</style>

      {/* Link Modal */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-primary mb-4">Insert Link</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary mb-2">
                URL
              </label>
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLinkSubmit();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    handleLinkCancel();
                  }
                }}
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="https://example.com"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleLinkCancel}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-secondary hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleLinkSubmit}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditorExtended;
